import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';

import Colors from 'pubweb-smokey/dist/colors';

import EnvelopeClosedSvg from 'pubweb-smokey/dist/images/svg/iconography-16x16/envelope-closed.svg';
import InfoCircleSvg from 'pubweb-smokey/dist/images/svg/iconography-16x16/info-circle.svg';

import Form from '@components/Shared/Form';
import TextField from '@components/Shared/TextField';
import Button from '@components/Shared/Buttons/Button';
import CheckBox from 'pubweb-smokey/dist/components/CheckBox';
import RadioButtonGroup from 'pubweb-smokey/dist/components/RadioButton/RadioButtonGroup';
import LoadingIndicator from '@components/Shared/LoadingIndicator/LoadingIndicator';
import TextArea from '@components/Shared/TextArea/TextArea';
import SelectableCard from '@components/Shared/SelectableCard/SelectableCard';

import { runCommonValidation } from 'pubweb-smokey/dist/utils/validation';
import { runSpecialValidation } from './SpecialValidation';
import { submitForm, submitCATForm } from '@services/formService';
import { pushGTMEvent } from 'pubweb-smokey/dist/utils/analytics';
import { pushGtmFormAction } from '@utils/gtmUtils';

import ThankYou from './ThankYou';

import { LocationsContext } from '@contexts/LocationsContext';
import { DXAccountContext } from '@contexts/DXAccountContext';

import { OwnAHomeStyles } from './OwnAHome.styled';
import { FormsStyles } from './Forms.styled';

const Forms = ({ onToggleHelpModal }) => {
  const [formErrors, setFormErrors] = useState({});
  const [isHomeownerFormVisible, setIsHomeownerFormVisible] = useState(false);
  const [isGeneralInquiryFormVisible, setIsGeneralInquiryFormVisible] =
    useState(false);
  const [isThankYouVisible, setIsThankYouVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [preferredContactMethod, setPreferredContactMethod] = useState(null);
  const [submitPressed, setSubmitPressed] = useState(false);

  const accountContext = useContext(DXAccountContext);
  const locationsContext = useContext(LocationsContext);

  const contactMethodItems = [
    { label: 'Phone', value: 'phone' },
    { label: 'Email', value: 'email' },
  ];

  const textAreaName = 'Comments (Max 500 characters)';

  // Put the button text here because DM would like to use button text for form_submit_text field.
  // Thought this would be easier to keep form_submit_text and button text in sync in case these ever change.
  const submitButtonText = 'Submit';

  const onValidate = (formObj) => {
    // Create the formObj.comments item because validation looks for it specifically and
    // the item in the form data uses the TextArea name field which most likely is not "comments".
    if (formObj[textAreaName]) {
      formObj.comments = formObj[textAreaName];
    }

    const formErrors = runCommonValidation(formObj);
    let additionalErrors = {};

    if (isHomeownerFormVisible) {
      additionalErrors = runSpecialValidation(
        formObj,
        preferredContactMethod,
        submitPressed
      );
    }

    const totalErrors = { ...formErrors, ...additionalErrors };
    setFormErrors(totalErrors);
    setSubmitPressed(false);

    if (totalErrors && totalErrors.hasErrors) {
      return totalErrors.hasErrors;
    }
  };

  const formObj = {};

  const onGeneralInquiryFormSubmit = (formObj) => {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);

    // Create the formObj.comments item because submit processing looks for it specifically and
    // the item in the form data uses the TextArea name field which most likely is not "comments".
    if (formObj[textAreaName]) {
      formObj.comments = formObj[textAreaName];
    }

    formObj.canEmail = true;
    formObj.originWebFormType = 'lead-form';
    formObj.originWebApp = 'clayton-homes-website';
    formObj.originWebPageType = 'clayton-homes-contact-us';
    formObj.dataExtension = 'FormActivityCH';

    submitForm(formObj)
      .then(() => {
        pushGTMEvent('contact-us-inquiry-form'); // This is the old UA event. It will remain for now, but we should remove in the future after DM is satisfied with new GA4 event.
        pushGtmFormAction({
          form_action_type: 'submit',
          form_name: 'contact_us',
          form_method: 'embedded',
          form_id: 'contact_us_general_inquiry',
          form_submit_text: submitButtonText
            .trim()
            .toLowerCase()
            .replaceAll(' ', '_'),
        });

        if (typeof window !== 'undefined') {
          window.analytics.identify(accountContext.state?.account?.userId, {
            firstName:
              accountContext.state.account?.firstName || formObj.firstName,
            lastName:
              accountContext.state.account?.lastName || formObj.lastName,
            phone: accountContext.state.account?.phone || formObj.phone,
            email: accountContext.state?.email || formObj.email,
            preferredContactMethod,
          });
        }

        setIsSubmitting(false);
        setIsThankYouVisible(true);
        setIsHomeownerFormVisible(false);
        setIsGeneralInquiryFormVisible(false);

        if (typeof window !== 'undefined') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      })
      .catch(() => {
        setIsSubmitting(false);
      });
  };

  const onHomeownerFormSubmit = (formObj) => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    // Create the formObj.comments item because submit processing looks for it specifically and
    // the item in the form data uses the TextArea name field which most likely is not "comments".
    if (formObj[textAreaName]) {
      formObj.comments = formObj[textAreaName];
    }

    formObj.preferredContactMethod = preferredContactMethod;

    submitCATForm(formObj)
      .then(() => {
        pushGTMEvent('contact-us-homeowner-form'); // This is the old UA event. It will remain for now, but we should remove in the future after DM is satisfied with new GA4 event.
        pushGtmFormAction({
          form_action_type: 'submit',
          form_name: 'contact_us',
          form_method: 'embedded',
          form_id: 'contact_us_home_owner',
          form_submit_text: submitButtonText
            .trim()
            .toLowerCase()
            .replaceAll(' ', '_'),
        });

        if (typeof window !== 'undefined') {
          window.analytics.identify(accountContext.state?.account?.userId, {
            firstName:
              accountContext.state.account?.firstName || formObj.firstName,
            lastName:
              accountContext.state.account?.lastName || formObj.lastName,
            phone: accountContext.state.account?.phone || formObj.phone,
            email: accountContext.state?.email || formObj.email,
            preferredContactMethod: formObj.preferredContactMethod,
          });
        }

        setIsSubmitting(false);
        setIsThankYouVisible(true);
        setIsHomeownerFormVisible(false);
        setIsGeneralInquiryFormVisible(false);

        if (typeof window !== 'undefined') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      })
      .catch(() => {
        setIsSubmitting(false);
      });
  };

  const onShowHomeownerForm = (ev) => {
    setIsHomeownerFormVisible(true);
    setIsGeneralInquiryFormVisible(false);
    setFormErrors({});
    setPreferredContactMethod(null);
    ev.preventDefault();
  };

  const onShowGeneralInquiryForm = (ev) => {
    setIsHomeownerFormVisible(false);
    setIsGeneralInquiryFormVisible(true);
    setFormErrors({});
    ev.preventDefault();
  };

  const onKeyDownHomeowner = (ev) => {
    // enter
    if (ev.keyCode === 13) {
      onShowHomeownerForm(ev);
    }
  };

  const onKeyDownInquiry = (ev) => {
    // enter
    if (ev && ev.keyCode === 13) {
      onShowGeneralInquiryForm(ev);
    }
  };

  const onKeyDownHelpInfo = (ev) => {
    // enter
    if (ev.keyCode === 13) {
      onToggleHelpModal();
    }
  };

  const updateMethod = (option) => {
    setPreferredContactMethod(option.value);
  };

  const formsClasses =
    isHomeownerFormVisible || isGeneralInquiryFormVisible ? '' : 'no-forms';
  /* eslint-disable react/no-unescaped-entities */
  return (
    <FormsStyles className={formsClasses}>
      <div className="form-header">
        <span>
          <EnvelopeClosedSvg viewBox="0 0 16 16" alt="Closed Envelope" />
          <h5>Send us a message</h5>
        </span>
      </div>

      <div id="form-content">
        {isThankYouVisible && <ThankYou />}
        {!isThankYouVisible && (
          <div>
            <div className="help-header">
              <p>How can we help?</p>
            </div>

            <div className="cards-container">
              <SelectableCard
                tabIndex="0"
                text="I own a Clayton home"
                id="homeownerCard"
                isSelected={isHomeownerFormVisible}
                onCardClick={onShowHomeownerForm}
                onKeyDown={onKeyDownHomeowner}
                name="homeownerCard"
                automationId="contact-us-homeowner-selectable-card"
                disabled={false}
              />
              <SelectableCard
                tabIndex="0"
                text="I have a general inquiry"
                id="inquiryCard"
                isSelected={isGeneralInquiryFormVisible}
                onCardClick={onShowGeneralInquiryForm}
                onKeyDown={onKeyDownInquiry}
                name="inquiryCard"
                automationId="contact-us-inquiry-selectable-card"
                disabled={false}
              />
            </div>
            {(isHomeownerFormVisible || isGeneralInquiryFormVisible) && (
              <Form
                onSubmit={
                  isHomeownerFormVisible
                    ? onHomeownerFormSubmit
                    : onGeneralInquiryFormSubmit
                }
                onFormValidate={onValidate}
              >
                <input
                  type="hidden"
                  name="source"
                  id="source"
                  defaultValue="ch-contact-us"
                />
                <input
                  type="hidden"
                  name="zip"
                  id="zip"
                  defaultValue={
                    accountContext.state.account?.postalCode ||
                    locationsContext.state.postalCode
                  }
                />

                <div className="shared-form-fields">
                  <div className="shared-form-fields-row">
                    <div className="form-field">
                      <TextField
                        id="firstName"
                        errorMessage={formErrors.firstName}
                        defaultValue={
                          accountContext.state.account?.firstName ||
                          formObj.firstName
                        }
                        label="First Name*"
                        textFieldStyle="primary"
                        automationId="contact-us-first-name-textfield"
                      />
                    </div>

                    <div className="form-field">
                      <TextField
                        id="lastName"
                        errorMessage={formErrors.lastName}
                        defaultValue={
                          accountContext.state.account?.lastName ||
                          formObj.lastName
                        }
                        label="Last Name*"
                        textFieldStyle="primary"
                        automationId="contact-us-last-name-textfield"
                      />
                    </div>
                  </div>

                  <div className="shared-form-fields-row">
                    <div className="form-field">
                      <TextField
                        id="phone"
                        errorMessage={formErrors.phone}
                        defaultValue={
                          accountContext.state.account?.phone || formObj.phone
                        }
                        label="Phone*"
                        textFieldStyle="primary"
                        automationId="contact-us-phone-textfield"
                      />
                    </div>

                    <div className="form-field">
                      <TextField
                        id="email"
                        errorMessage={formErrors.email}
                        defaultValue={
                          accountContext.state?.email || formObj.email
                        }
                        label="Email*"
                        textFieldStyle="primary"
                        automationId="contact-us-email-textfield"
                      />
                    </div>
                  </div>
                </div>

                {isHomeownerFormVisible && (
                  <OwnAHomeStyles>
                    <div className="preferred-contact">
                      <p className="preferred-contact-title">
                        Preferred Contact Method*
                      </p>
                      <div className="radios">
                        <RadioButtonGroup
                          id="prefContactMethods"
                          items={contactMethodItems}
                          isLayoutHorizontal={true}
                          onChange={updateMethod}
                        />
                      </div>
                    </div>
                    {formErrors.preferredContact ? (
                      <p className="contact-error" key="contact-error">
                        {formErrors.preferredContact}
                      </p>
                    ) : (
                      <p className="contact-error">&nbsp;</p>
                    )}

                    <div className="form-field serial-no-field">
                      <TextField
                        id="serial"
                        errorMessage={formErrors.serial}
                        defaultValue={formObj.serial}
                        label="Serial Number*"
                        textFieldStyle="primary"
                        automationId="contact-us-serial-textfield"
                      />
                    </div>

                    <p
                      className="help-link"
                      onClick={onToggleHelpModal}
                      onKeyDown={onKeyDownHelpInfo}
                      tabIndex="0"
                    >
                      <InfoCircleSvg viewBox="0 0 16 16" alt="Help" />
                      How to find your home's serial number
                    </p>

                    <div className="shared-form-fields">
                      <div className="shared-form-fields-row">
                        <div className="form-field">
                          <TextField
                            id="location"
                            errorMessage={formErrors.location}
                            defaultValue={formObj.location}
                            label="Purchase Location"
                            textFieldStyle="primary"
                            automationId="contact-us-location-textfield"
                          />
                        </div>

                        <div className="form-field">
                          <TextField
                            id="year"
                            errorMessage={formErrors.year}
                            defaultValue={formObj.year}
                            label="Purchase Year"
                            textFieldStyle="primary"
                            automationId="contact-us-year-textfield"
                          />
                        </div>
                      </div>
                    </div>
                  </OwnAHomeStyles>
                )}

                <span className="comments">
                  <TextArea
                    animated={true}
                    error={isHomeownerFormVisible ? formErrors.comments : false}
                    errorMessage={
                      isHomeownerFormVisible && formErrors.comments
                        ? formErrors.comments
                        : ''
                    }
                    inputName={textAreaName}
                    inputId="comments"
                    fillSpace={true}
                    focusColor={Colors.primary.claytonBlue.standard}
                    inputMaxLength="500"
                  />
                </span>

                {isGeneralInquiryFormVisible && (
                  <div>
                    <div className="terms-container">
                      <CheckBox
                        id="termsConditions"
                        automationId="terms-conditions-checkbox"
                      ></CheckBox>
                      <p className="xsmall">
                        <span>
                          By checking this box, I agree that Clayton may contact
                          me by telephone, provide me with marketing
                          communications about Clayton products and services,
                          and share my contact information as described in our
                          website{' '}
                        </span>
                        <a href="https://www.claytonhomes.com/account-terms-conditions/">
                          Terms &amp; Conditions
                        </a>
                        <span> and </span>
                        <a href="https://www.claytonhomes.com/privacy/">
                          Privacy Policy
                        </a>
                        <span>.</span>
                      </p>
                    </div>
                    {formErrors.termsConditions ? (
                      <p className="terms-error" key="terms-error">
                        Please check the box to agree to the Terms &amp;
                        Conditions.*
                      </p>
                    ) : (
                      <p className="terms-error">&nbsp;</p>
                    )}
                  </div>
                )}

                <Button
                  aria-label="Submit"
                  automationId="contact-us-submit"
                  fillContainer={true}
                  disabled={isSubmitting}
                  type="submit"
                  onClick={() => setSubmitPressed(true)}
                >
                  {!isSubmitting ? (
                    submitButtonText
                  ) : (
                    <LoadingIndicator
                      color={Colors.primary.white.standard}
                      width={24}
                      height={24}
                    />
                  )}
                </Button>
              </Form>
            )}
          </div>
        )}
      </div>
    </FormsStyles>
  );
  /* eslint-enable react/no-unescaped-entities */
};

Forms.propTypes = {
  onToggleHelpModal: PropTypes.func,
};

export default Forms;
